import { Dispatch, useState } from 'react';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  styled,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { IDeaEditMode, TooltipTrigger, Tooltip } from '../common';
import { CsaFilterWrapper } from '../csafilter';
import { RequestAction, ImportFile, RegistrationNo } from '.';
import { pathOr } from 'rambda';
import { map as lmap, compose as lcompose } from 'lodash/fp';

enum DeaSelection {
  ADD_NEW = 'ADD_NEW',
  FILE_IMPORT = 'FILE_IMPORT',
  FROM_LIST = 'FROM_LIST',
}

export const AddDeaNum = ({
  deaNos,
  dispatchRequest,

  tooltipValue,
  toggleTooltip,
  offTooltip,
  onSave,
  feedback,
}: {
  deaNos: IDeaEditMode[];
  dispatchRequest: Dispatch<RequestAction>;

  tooltipValue: (tooltip: string) => boolean;
  toggleTooltip: (tooltip: string) => void;
  offTooltip: (tooltip: string) => void;
  onSave: () => void;
  feedback: string;
}) => {
  const [deaSelection, setDeaSelection] = useState<DeaSelection>(
    DeaSelection.ADD_NEW
  );

  const onChangeDeaSelectionRadio = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDeaSelection(event.target.value as DeaSelection);
  };

  const create = (a: IDeaEditMode) =>
    dispatchRequest({ type: 'CreateDeaNum', payload: a });

  const renderDeaNumSelectorGroups = () => {
    return (
      <Tooltip name="requestDeaNumOption" tooltipValue={tooltipValue}>
        <DeaRadioGroup
          row
          aria-label="dea-number-selection-options"
          name="dea-number-selection-options"
          defaultValue={DeaSelection.ADD_NEW}
          onChange={onChangeDeaSelectionRadio}>
          <DeaRadio
            value={DeaSelection.ADD_NEW}
            control={<Radio color="primary" />}
            label={<span>Add DEA Number</span>}
            labelPlacement="start"
          />
          <DeaRadio
            value={DeaSelection.FILE_IMPORT}
            control={<Radio color="primary" />}
            label={<span>Import File</span>}
            labelPlacement="start"
          />
          <DeaRadio
            value={DeaSelection.FROM_LIST}
            control={<Radio color="primary" />}
            label={<span>Select from List</span>}
            labelPlacement="start"
          />
        </DeaRadioGroup>
      </Tooltip>
    );
  };

  const renderDeaNumChoices = () => {
    switch (deaSelection) {
      case DeaSelection.FILE_IMPORT:
        return (
          <Actions>
            <ImportFile
              title="Import DEA Number. Click to select a txt file..."
              onImport={payload =>
                dispatchRequest({ type: 'CreateDeaNums', payload })
              }
            />
            <TooltipTrigger
              name="importDeaNum"
              toggleTooltip={toggleTooltip}
              offTooltip={offTooltip}
            />
            <Tooltip
              name="importDeaNum"
              title="rich"
              tooltipValue={tooltipValue}>
              <a href="assets/sample.txt" download="sample.txt">
                DEA Registration Number sample file
              </a>
            </Tooltip>
          </Actions>
        );
      case DeaSelection.FROM_LIST:
        return (
          <CsaFilterWrapper
            deaNums={lmap<IDeaEditMode, string>(pathOr('', 'deaNum'))(deaNos)}
            save={lcompose(onSave, create)}
            tooltipValue={tooltipValue}
            toggleTooltip={toggleTooltip}
            offTooltip={offTooltip}
          />
        );
      default:
      case DeaSelection.ADD_NEW:
        return (
          <RegistrationNo
            item={{
              key: uuidv4(),
              deaNum: '',
              editMode: 'Create',
              msgCode: '',
              msgText: '',
              serialNumber: null,
            }}
            save={create}
            tooltipValue={tooltipValue}
            toggleTooltip={toggleTooltip}
            offTooltip={offTooltip}
            feedback={feedback}
          />
        );
    }
  };

  return (
    <AddDeaNumSection>
      <FormLabel>
        <span>DEA Number Selection Options </span>
        <TooltipTrigger
          name="requestDeaNumOption"
          toggleTooltip={toggleTooltip}
          offTooltip={offTooltip}
        />
        {`: `}
      </FormLabel>
      {renderDeaNumSelectorGroups()}
      {renderDeaNumChoices()}
    </AddDeaNumSection>
  );
};

const DeaRadioGroup = styled(RadioGroup)(({ theme }) => ({
  alignItems: 'center',
}));
const DeaRadio = styled(FormControlLabel)(({ theme }) => ({
  marginRight: '2rem',
  marginBottom: '1rem',
  marginTop: '1rem',
  flexGrow: 1,
}));
const Actions = styled('article')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',

  justifyContent: 'center',
  '@media (min-width: 40em)': {
    justifyContent: 'start',
    '&>*+*': {
      marginLeft: '1rem',
    },
  },
}));

const AddDeaNumSection = styled('section')(({ theme }) => ({
  padding: '24px',

  '&>*+*': {
    marginTop: '1rem',
  },
}));
